<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="primary">mdi-account-tie</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Total Vendedores</div>
              <div class="kpi-value">{{ totalVendors }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="success">mdi-check-circle</v-icon>
                {{ activeVendors }} activos
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="success">mdi-account-check</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Activos</div>
              <div class="kpi-value">{{ activeVendors }}</div>
              <div class="kpi-subtitle">{{ activePercentage }}% del total</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="warning">mdi-account-clock</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Pendientes</div>
              <div class="kpi-value">{{ pendingVendors }}</div>
              <div class="kpi-subtitle">Requieren activación</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="info">mdi-email-check</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Con Email</div>
              <div class="kpi-value">{{ vendorsWithEmail }}</div>
              <div class="kpi-subtitle">{{ emailPercentage }}% contactables</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Card -->
    <v-card class="main-card elevation-0" outlined>
      <!-- Header with Actions -->
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title">Gestión de Vendedores</h2>
          <v-spacer></v-spacer>

          <!-- Search -->
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar vendedor..."
            single-line
            hide-details
            dense
            outlined
            clearable
            class="mr-4 search-field"
            style="max-width: 300px"
          ></v-text-field>

          <v-btn color="primary" @click="openDialog">
            <v-icon left>mdi-plus</v-icon>
            Nuevo Vendedor
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="vendors"
        :loading="loading"
        loader-height="3"
        loading-text="Cargando vendedores..."
        :search="search"
        class="minimal-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
      >
        <!-- Vendor Info -->
        <template v-slot:[`item.vendor`]="{ item }">
          <div class="vendor-info">
            <div class="vendor-name">
              <v-icon small class="mr-2" color="primary">mdi-account-tie</v-icon>
              {{ item.nombre }}
            </div>
            <div class="vendor-details">
              <span class="mr-3">
                <v-icon x-small class="mr-1">mdi-barcode</v-icon>
                {{ item.codigo }}
              </span>
              <span>
                <v-icon x-small class="mr-1">mdi-card-account-details</v-icon>
                {{ item.cedula }}
              </span>
            </div>
          </div>
        </template>

        <!-- Contact Info -->
        <template v-slot:[`item.contact`]="{ item }">
          <div class="contact-info">
            <div v-if="item.email" class="contact-email">
              <v-icon x-small class="mr-1">mdi-email</v-icon>
              {{ item.email }}
            </div>
            <div v-if="item.celular" class="contact-phone">
              <v-icon x-small class="mr-1">mdi-cellphone</v-icon>
              {{ item.celular }}
            </div>
            <div v-if="!item.email && !item.celular" class="grey--text">
              Sin información de contacto
            </div>
          </div>
        </template>

        <!-- Store -->
        <template v-slot:[`item.store`]="{ item }">
          <v-chip x-small outlined>
            <v-icon x-small left>mdi-store</v-icon>
            {{ item.store }}
          </v-chip>
        </template>

        <!-- Status -->
        <template v-slot:[`item.estado`]="{ item }">
          <v-chip
            :color="getStatusColor(item.estado)"
            :text-color="getStatusTextColor(item.estado)"
            small
            label
          >
            <v-icon x-small left>{{ getStatusIcon(item.estado) }}</v-icon>
            {{ item.estado }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            small
            color="primary"
            @click="editItem(item)"
            title="Editar vendedor"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>

        <!-- No data -->
        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-account-tie-off</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay vendedores registrados</h3>
            <v-btn color="primary" class="mt-4" @click="openDialog">
              <v-icon left>mdi-plus</v-icon>
              Agregar Primer Vendedor
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="700px" persistent>
      <v-card class="elevation-0">
        <v-toolbar flat class="minimal-toolbar">
          <h3 class="dialog-title">
            <v-icon small class="mr-2">mdi-account-tie</v-icon>
            {{ formTitle }}
          </h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-form ref="form" v-model="valid" lazy-validation>
          <v-card-text class="pa-6">
            <!-- Información Básica -->
            <h4 class="form-section-title mb-4">
              <v-icon small class="mr-2">mdi-information</v-icon>
              Información Básica
            </h4>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.codigo"
                  :rules="codeRules"
                  label="Código del Vendedor"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-barcode"
                  hide-details="auto"
                  :disabled="editedIndex !== -1"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.cedula"
                  :rules="idRules"
                  label="Cédula / ID"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-card-account-details"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.nombre"
                  :rules="nameRules"
                  label="Nombre Completo"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Información de Contacto -->
            <h4 class="form-section-title mb-4">
              <v-icon small class="mr-2">mdi-contacts</v-icon>
              Información de Contacto
            </h4>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.celular"
                  :rules="phoneRules"
                  label="Teléfono Celular"
                  outlined
                  dense
                  prepend-inner-icon="mdi-cellphone"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.email"
                  :rules="emailRules"
                  label="Correo Electrónico"
                  outlined
                  dense
                  prepend-inner-icon="mdi-email"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Estado -->
            <h4 class="form-section-title mb-4">
              <v-icon small class="mr-2">mdi-toggle-switch</v-icon>
              Estado del Vendedor
            </h4>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.estado"
                  :items="statusOptions"
                  :rules="statusRules"
                  label="Estado"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-check-circle"
                  hide-details="auto"
                >
                  <template v-slot:item="{ item }">
                    <v-icon small left :color="getStatusColor(item)">
                      {{ getStatusIcon(item) }}
                    </v-icon>
                    {{ item }}
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Información adicional -->
            <v-alert type="info" dense text class="mt-4" v-if="editedIndex === -1">
              <v-icon small class="mr-2">mdi-information</v-icon>
              El código del vendedor no podrá ser modificado después de crearlo
            </v-alert>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="close"> Cancelar </v-btn>
            <v-btn color="primary" :disabled="!valid" :loading="saving" @click="save">
              <v-icon left>mdi-content-save</v-icon>
              {{ editedIndex === -1 ? "Crear" : "Actualizar" }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver } from "../../services/webserver.js";

export default {
  name: "Vendors",
  data() {
    return {
      dialog: false,
      loading: false,
      saving: false,
      search: "",
      valid: true,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",

      // Headers modernizados
      headers: [
        {
          text: "Vendedor",
          value: "vendor",
          sortable: false,
          width: "35%",
        },
        {
          text: "Contacto",
          value: "contact",
          sortable: false,
          width: "30%",
        },
        {
          text: "Tienda",
          value: "store",
          align: "center",
          width: "15%",
        },
        {
          text: "Estado",
          value: "estado",
          align: "center",
          width: "15%",
        },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          align: "center",
          width: "5%",
        },
      ],

      statusOptions: ["Activo", "Inactivo", "Pendiente"],
      vendors: [],
      editedIndex: -1,
      editedItem: {
        store: "",
        codigo: "",
        cedula: "",
        nombre: "",
        estado: "",
        email: "",
        celular: "",
      },
      defaultItem: {
        store: "",
        codigo: "",
        cedula: "",
        nombre: "",
        estado: "Activo",
        email: "",
        celular: "",
      },

      // Reglas de validación
      codeRules: [
        (v) => !!v || "El código es requerido",
        (v) => (v && v.length >= 3) || "El código debe tener al menos 3 caracteres",
      ],
      idRules: [
        (v) => !!v || "La cédula es requerida",
        (v) => (v && v.length >= 6) || "La cédula debe tener al menos 6 caracteres",
      ],
      nameRules: [
        (v) => !!v || "El nombre es requerido",
        (v) => (v && v.length >= 3) || "El nombre debe tener al menos 3 caracteres",
      ],
      phoneRules: [(v) => !v || /^[0-9+\-\s]+$/.test(v) || "El teléfono debe ser válido"],
      emailRules: [(v) => !v || /.+@.+\..+/.test(v) || "El correo debe ser válido"],
      statusRules: [(v) => !!v || "El estado es requerido"],
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "Nuevo Vendedor" : "Editar Vendedor";
    },

    totalVendors() {
      return this.vendors.length;
    },

    activeVendors() {
      return this.vendors.filter((v) => v.estado === "Activo").length;
    },

    pendingVendors() {
      return this.vendors.filter((v) => v.estado === "Pendiente").length;
    },

    vendorsWithEmail() {
      return this.vendors.filter((v) => v.email && v.email.trim() !== "").length;
    },

    activePercentage() {
      if (this.totalVendors === 0) return 0;
      return Math.round((this.activeVendors / this.totalVendors) * 100);
    },

    emailPercentage() {
      if (this.totalVendors === 0) return 0;
      return Math.round((this.vendorsWithEmail / this.totalVendors) * 100);
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  created() {
    this.get_vendors();
  },

  methods: {
    async get_vendors() {
      const qry = {
        store: window.profile.store_id,
        dbSearch: "inventory",
        filters: [
          {
            field: "id_pos",
            operator: "=",
            value: window.profile.store_id,
          },
        ],
      };

      this.loading = true;

      try {
        const result = await new Promise((resolve, reject) => {
          webserver(
            "vendors",
            qry,
            (data) => resolve(data),
            () => reject([])
          );
        });

        this.vendors = result || [];
        this.showSnackbar(`${this.vendors.length} vendedores cargados`, "success");
      } catch (error) {
        this.vendors = [];
        this.showSnackbar("Error al cargar vendedores", "error");
      } finally {
        this.loading = false;
      }
    },

    editItem(item) {
      this.editedIndex = this.vendors.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    openDialog() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.dialog = true;
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },

    async save() {
      if (!this.$refs.form.validate()) {
        this.showSnackbar("Por favor complete todos los campos requeridos", "warning");
        return;
      }

      delete this.editedItem.qty;
      this.editedItem.store = window.profile.store_id;
      this.saving = true;

      const qry = {
        store: window.profile.store_id,
        data: [this.editedItem],
      };

      try {
        const result = await new Promise((resolve, reject) => {
          webserver(
            "vendors_update",
            qry,
            (data) => resolve(data),
            () => reject([])
          );
        });

        if (result && result.length > 0) {
          this.showSnackbar(
            this.editedIndex === -1
              ? "Vendedor creado exitosamente"
              : "Vendedor actualizado exitosamente",
            "success"
          );
          this.dialog = false;
          this.get_vendors();
        } else {
          this.showSnackbar("Error al guardar vendedor", "error");
        }
      } catch (error) {
        this.showSnackbar("Error al guardar vendedor", "error");
      } finally {
        this.saving = false;
      }
    },

    getStatusColor(status) {
      const colors = {
        Activo: "green lighten-4",
        Inactivo: "grey lighten-3",
        Pendiente: "orange lighten-4",
      };
      return colors[status] || "grey lighten-3";
    },

    getStatusTextColor() {
      return "black";
    },

    getStatusIcon(status) {
      const icons = {
        Activo: "mdi-check-circle",
        Inactivo: "mdi-cancel",
        Pendiente: "mdi-clock-outline",
      };
      return icons[status] || "mdi-help-circle";
    },

    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.dialog-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

/* Form sections */
.form-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #666;
  display: flex;
  align-items: center;
}

/* Search Field */
.search-field >>> .v-input__control {
  background-color: white;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 16px !important;
  font-size: 0.875rem !important;
}

/* Vendor Info */
.vendor-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vendor-name {
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
}

.vendor-details {
  font-size: 0.75rem;
  color: #666;
}

/* Contact Info */
.contact-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.contact-email,
.contact-phone {
  font-size: 0.75rem;
  color: #666;
  display: flex;
  align-items: center;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Responsive */
@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
  }

  .minimal-toolbar {
    flex-wrap: wrap;
    gap: 16px;
  }
}
</style>
